import { ArrowRightEndOnRectangleIcon, BuildingOfficeIcon, ChartBarIcon, ClipboardDocumentListIcon, Cog6ToothIcon, GlobeAltIcon, LinkIcon, MapPinIcon, SparklesIcon, SwatchIcon, TagIcon, TruckIcon, UserCircleIcon, UsersIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback } from '@/components/Ui/Avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/Ui/DropdownMenu';
import { SettingsDialog } from '@/dialogs/SettingsDialog';
import auth from '@/helpers/auth';
import { useLogout } from '@/hooks/useAuth';

interface UserMenuProps {
    onSettingsSave?: () => void;
}

export function UserMenu({ onSettingsSave }: UserMenuProps) {
    const [
        t,
    ] = useTranslation('pages');
    const isAdminstrator = auth.isAdministrator();
    const canViewActivities = auth.can('activities:view');
    const canManageExact = auth.can('exact_connections:manage');
    const [
        settingsOpen,
        setSettingsOpen,
    ] = useState(false);
    const navigate = useNavigate();
    const logout = useLogout();

    const handleLogout = async () => {
        await logout.mutateAsync();

        navigate('/login');

        return false;
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    // Generate initials from name (max 2 characters)
    const getInitials = (name: string) => {
        const parts = name.trim().split(/\s+/);

        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const userName = `${ auth.getUser()?.first_name } ${ auth.getUser()?.last_name ?? '' }`.trim();
    const initials = getInitials(userName);

    const handleSettingsClick = () => {
        setSettingsOpen(true);
    };

    return (
        <>
            <div className='flex items-center'>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className='flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer'>
                            {/* Mobile: Avatar with initials */}
                            <Avatar className='h-8 w-8 border border-border md:hidden'>
                                <AvatarFallback className='bg-primary/10 text-primary text-xs font-medium'>
                                    {initials}
                                </AvatarFallback>
                            </Avatar>
                            <span className='hidden md:flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors'>
                                <span className='max-w-[160px] truncate'>{userName}</span>
                                <ChevronDownIcon className='h-4 w-4 text-muted-foreground' />
                            </span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end' className='w-56'>
                        {/* Show full name on mobile in dropdown */}
                        <div className='px-2 py-1.5 md:hidden'>
                            <p className='text-sm font-medium text-foreground'>{userName}</p>
                        </div>
                        <DropdownMenuSeparator className='md:hidden' />
                        {isAdminstrator && canViewActivities && (
                            <>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/activities'>
                                        <ClipboardDocumentListIcon className='h-4 w-4 mr-2' />
                                        {t('Activities')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {isAdminstrator && (
                            <>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/brands'>
                                        <SparklesIcon className='h-4 w-4 mr-2' />
                                        {t('Brands')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/companies'>
                                        <BuildingOfficeIcon className='h-4 w-4 mr-2' />
                                        {t('Companies')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/providers'>
                                        <TruckIcon className='h-4 w-4 mr-2' />
                                        {t('Providers')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/production-batches'>
                                        <SwatchIcon className='h-4 w-4 mr-2' />
                                        {t('Production')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/users'>
                                        <UsersIcon className='h-4 w-4 mr-2' />
                                        {t('Users')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/discount-rules'>
                                        <TagIcon className='h-4 w-4 mr-2' />
                                        {t('Discount Rules')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/shipping-locations'>
                                        <MapPinIcon className='h-4 w-4 mr-2' />
                                        {t('Shipping Locations')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/countries'>
                                        <GlobeAltIcon className='h-4 w-4 mr-2' />
                                        {t('Countries')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/analysis/stock-valuation'>
                                        <ChartBarIcon className='h-4 w-4 mr-2' />
                                        {t('Analysis')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {isAdminstrator && canManageExact && (
                            <>
                                <DropdownMenuItem asChild className='cursor-pointer'>
                                    <NavLink className='flex items-center' to='/exact-connection'>
                                        <LinkIcon className='h-4 w-4 mr-2' />
                                        {t('Exact')}
                                    </NavLink>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        <DropdownMenuItem className='cursor-pointer' onClick={ handleProfileClick }>
                            <UserCircleIcon className='h-4 w-4 mr-2' />
                            {t('Profile')}
                        </DropdownMenuItem>
                        <DropdownMenuItem className='cursor-pointer md:hidden' onClick={ handleSettingsClick }>
                            <Cog6ToothIcon className='h-4 w-4 mr-2' />
                            {t('Settings')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className='cursor-pointer text-destructive hover:text-white focus:text-white' onClick={ handleLogout }>
                            <ArrowRightEndOnRectangleIcon className='h-4 w-4 mr-2' />
                            {t('Logout')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Settings icon — standalone, opens settings directly */}
                <button
                    aria-label='Settings'
                    className='hidden md:flex p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer'
                    onClick={ handleSettingsClick }
                >
                    <Cog6ToothIcon className='h-5 w-5' />
                </button>
            </div>
            <SettingsDialog
                onChange={ onSettingsSave }
                onOpenChange={ setSettingsOpen }
                open={ settingsOpen }
            />
        </>
    );
}
